/**
 * useSectionUrlSync
 *
 * Keeps the browser URL in sync with the most-visible section of the
 * single-page home page, without ever reloading the route.
 *
 * Approach:
 * - Throttled scroll listener finds the section whose top is closest to
 *   the viewport top (with a small offset for the fixed navbar) and
 *   silently calls history.replaceState.
 * - On first load, if URL is `/<slug>`, scroll to that section once
 *   the DOM is ready.
 *
 * Section IDs are expected to match the slugs (e.g. <section id="design">).
 */
import { useEffect } from 'react';

const SECTIONS = [
  { slug: '', id: 'home' },
  { slug: 'design', id: 'design' },
  { slug: 'concept', id: 'concept' },
  { slug: 'amenities', id: 'amenities' },
  { slug: 'location', id: 'location' },
  { slug: 'pricing', id: 'pricing' },
  { slug: 'news-events', id: 'news' },
  { slug: 'contact', id: 'contact' },
];

const ID_TO_SLUG = SECTIONS.reduce((acc, s) => {
  acc[s.id] = s.slug;
  return acc;
}, {});

const SLUG_TO_ID = SECTIONS.reduce((acc, s) => {
  acc[s.slug] = s.id;
  return acc;
}, {});

const NAV_OFFSET = 120; // navbar height + a little buffer

const setUrl = (slug, mode = 'replace') => {
  const target = '/' + (slug || '');
  if (window.location.pathname === target) return;
  try {
    window.history[mode === 'push' ? 'pushState' : 'replaceState'](
      { sectionSlug: slug },
      '',
      target + window.location.search,
    );
  } catch (_) {
    /* old browsers, ignore */
  }
};

export const useSectionUrlSync = () => {
  // Deep-link scroll on first mount.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const initialSlug = window.location.pathname.replace(/^\//, '').replace(/\/$/, '');
    if (!initialSlug || !SLUG_TO_ID[initialSlug]) return undefined;

    let attempts = 0;
    const id = SLUG_TO_ID[initialSlug];
    const interval = setInterval(() => {
      const el = document.getElementById(id);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET + 80;
        window.scrollTo({ top, behavior: 'auto' });
        clearInterval(interval);
      }
      attempts += 1;
      if (attempts > 20) clearInterval(interval); // give up after ~3s
    }, 150);
    return () => clearInterval(interval);
  }, []);

  // Throttled scroll listener — finds the section whose top is just above the
  // navbar offset and updates the URL to its slug.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let lastSlug = null;
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const scrollPos = window.scrollY + NAV_OFFSET;
        let activeId = 'home';

        // Walk sections in order; the last section whose top is <= scrollPos wins.
        for (const s of SECTIONS) {
          const el = document.getElementById(s.id);
          if (el && el.offsetTop <= scrollPos) {
            activeId = s.id;
          }
        }

        // If user is at the very bottom of the page, force the last section.
        if (window.innerHeight + window.scrollY >= document.body.scrollHeight - 5) {
          activeId = SECTIONS[SECTIONS.length - 1].id;
        }

        const slug = ID_TO_SLUG[activeId];
        if (slug !== undefined && slug !== lastSlug) {
          lastSlug = slug;
          setUrl(slug, 'replace');
        }
        ticking = false;
      });
    };

    // Run once after a tick so it picks up the initial section after deep-link scroll.
    const initial = setTimeout(onScroll, 600);

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearTimeout(initial);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);
};

// Helper for nav clicks: scroll smoothly + push history entry so Back works.
export const navigateToSection = (slug) => {
  const id = SLUG_TO_ID[slug];
  setUrl(slug, 'push');
  if (!id) return;
  const el = document.getElementById(id);
  if (el) {
    const top = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET + 80;
    window.scrollTo({ top, behavior: 'smooth' });
  } else if (slug === '') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

export const SECTION_LIST = SECTIONS;
